/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../theme/theme"
import { css } from "@mui/material"
import { MyHeader } from "../../components/text/MyHeader"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../util/isMobile"
import { useState, useEffect } from "react"
import { useIsMobile } from "../../util/useIsMobile"
import { ProgressBar } from "../../components/ProgressBar"
import { PageIndicator } from "../../components/PageIndicator"
import { ViewPager } from "../../components/ViewPager"
import { QuestionnaireFormProvider, useQuestionnaireForm } from "./context/QuestionnaireFormContext"
import { formSteps } from "../common/constants/formSteps"
import { MobileNavigationButtons } from "./MobileNavigationButtons"

const QuestionnaireStyle = withMyTheme((theme) => css`
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    
    ${mobileCss(`
        width: 100vw;
        height: 100vh;
        overflow: hidden;
    `)}
`)

const QuestionnaireContentStyle = withMyTheme((theme) => css`
    max-width: 80vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 8vh;
    
    ${mobileCss(`
        height: 100%;
        width: 100%;
        gap: 1rem;
        justify-content: space-between;
    `)}
`)

const NavigationContainerStyle = withMyTheme(() => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60vw;
    max-width: 80vw;
    margin-top: 5vh;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        max-width: none;
        flex-shrink: 0;
    `)}
`)



const questionnaireSteps = formSteps

const StepContainerStyle = withMyTheme((theme) => css`
    max-width: 90vw;
    ${mobileCss(`
        max-width: 70vw;
    `)}
`)

const ViewPagerWrapperStyle = withMyTheme((theme) => css`
    ${mobileCss(`
        flex: 0 1 auto;
        max-height: 55vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        overflow: visible;
    `)}
`)

const HeaderContainerStyle = withMyTheme((theme) => css`
text-align: center;
    ${mobileCss(`
        flex-shrink: 0;
    `)}
`)

const MobileNavigationContainerStyle = withMyTheme((theme) => css`
    ${mobileCss(`
        flex-shrink: 0;
        padding: 0 5vw 3vh 5vw;
        width: 100%;
        box-sizing: border-box;
    `)}
`)

const QuestionnaireContent = () => {
    const { t } = useTranslation()
    const { formData, updateCurrentStep } = useQuestionnaireForm()
    const [currentStep, setCurrentStep] = useState(0)
    const isMobile = useIsMobile()

    const totalSteps = questionnaireSteps.length

    // Load saved step on component mount
    useEffect(() => {
        setCurrentStep(formData.currentStep)
    }, [formData.currentStep])

    const handleStepChange = (step: number) => {
        setCurrentStep(step)
        updateCurrentStep(step)
    }

    const handlePageClick = (index: number) => {
        const newStep = index + 1 // Adjust for intro step
        setCurrentStep(newStep)
        updateCurrentStep(newStep)
    }

    const handleStartQuestionnaire = () => {
        const newStep = 1 // Move to first actual questionnaire step
        setCurrentStep(newStep)
        updateCurrentStep(newStep)
    }

    const handlePreviousStep = () => {
        if (currentStep > 1) {
            const newStep = currentStep - 1
            setCurrentStep(newStep)
            updateCurrentStep(newStep)
        }
    }

    const handleNextStep = () => {
        if (currentStep < totalSteps - 1) {
            const newStep = currentStep + 1
            setCurrentStep(newStep)
            updateCurrentStep(newStep)
        }
    }

    return (
        <div css={QuestionnaireStyle}>
            <div css={QuestionnaireContentStyle}>
                <div css={HeaderContainerStyle}>
                    <MyHeader text={t('questionnaire.title')} />
                </div>

                {/* Show progress bar only after intro step */}
                <div css={NavigationContainerStyle}>
                    <ProgressBar
                        currentStep={currentStep - 1} // Adjust for intro step
                        totalSteps={totalSteps - 1} // Exclude intro step from total
                    />
                </div>

                {/* View Pager with Navigation */}
                <div css={ViewPagerWrapperStyle}>
                    <ViewPager
                        currentIndex={currentStep}
                        onIndexChange={handleStepChange}
                        showNavigation={!isMobile && currentStep > 0} // Hide navigation on mobile and intro step
                    >
                        {questionnaireSteps.map((step) => {
                            const StepComponent = step.component
                            return (
                                <div key={step.id} css={StepContainerStyle}>
                                    <StepComponent onStart={step.isIntro ? handleStartQuestionnaire : undefined} />
                                </div>
                            )
                        })}
                    </ViewPager>
                </div>

                {/* Page Indicators - Desktop only, show after intro step */}
                {!isMobile && currentStep > 0 && (
                    <PageIndicator
                        totalPages={totalSteps - 1} // Exclude intro step
                        currentPage={currentStep - 1} // Adjust for intro step
                        onPageClick={handlePageClick}
                    />
                )}

                {/* Mobile Navigation Buttons - only show after intro step */}
                {isMobile && currentStep > 0 && (
                    <div css={MobileNavigationContainerStyle}>
                        <MobileNavigationButtons
                            currentStep={currentStep}
                            totalSteps={totalSteps}
                            onPreviousStep={handlePreviousStep}
                            onNextStep={handleNextStep}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export const Questionnaire = () => {
    return (
        <QuestionnaireFormProvider>
            <QuestionnaireContent />
        </QuestionnaireFormProvider>
    )
}

